import {
  ChevronLeftIcon,
  ChevronRightIcon,
  Cross2Icon,
} from "@radix-ui/react-icons";
import moment from "moment";
import {
  CSSProperties,
  Fragment,
  MouseEvent,
  PointerEvent,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

export const HOUR_PX = 48;
/** The hour labels' column (w-12, and the 48px in the in-view width). */
const GUTTER_PX = 48;
const SLOT_MINUTES = 15;
const MIN_BLOCK_PX = 14;
/** How far This meeting moves before a press on it is a drag. */
const DRAG_PIXELS = 4;
/** Dragging This meeting this close to an edge (or past it) scrolls the grid. */
const EDGE_PX = 32;
/** The fastest that scroll goes, in pixels a frame. */
const EDGE_SCROLL_PX = 16;
/** The column headers' shortest height (two lines: title and subtitle). */
const HEADER_PX = 48;

/** A stretch of a column, in minutes from the start of its day. */
export type Span = { from: number; to: number };

/** A booking shown as busy; pending: a request not accepted yet (dashed). */
export type Block = Span & { id: number; label: string; pending?: boolean };

/**
 * One tutor's (or a room's) part of a column: their bookings and, for
 * tutors, their free time. Free time is shading only; any time can be
 * picked.
 */
export type Lane = { key: string; blocks: Block[]; free?: Span[] };

export type Column = {
  key: string;
  title: ReactNode;
  subtitle?: ReactNode;
  /** The header's hover text, in full (names and addresses get cut off). */
  hint: string;
  /** Bold green title: the selected day. */
  highlighted?: boolean;
  /** For this meeting's time, beside the subtitle. */
  badge?: { text: string; tone: "free" | "some" | "busy" };
  /**
   * Side by side; with several, each in its lane color (LANE_COLORS, by
   * position). One lane looks as a single tutor or room always has.
   */
  lanes: Lane[];
  /** This meeting's box; red when it clashes. */
  thisMeeting?: Span & { clash: boolean };
  /** Tooltip for clicking the column. */
  pickTitle: string;
};

/**
 * Several tutors' lanes: free time in a light tint of their color, bookings
 * in a darker shade, in the order the tutors were chosen.
 */
export const LANE_COLORS = [
  {
    swatch: "bg-green-300",
    free: "bg-green-100",
    booked: "border-green-400 bg-green-300 text-green-900",
    request: "border-green-500 text-green-900",
  },
  {
    swatch: "bg-sky-300",
    free: "bg-sky-100",
    booked: "border-sky-400 bg-sky-300 text-sky-900",
    request: "border-sky-500 text-sky-900",
  },
  {
    swatch: "bg-amber-300",
    free: "bg-amber-100",
    booked: "border-amber-400 bg-amber-300 text-amber-900",
    request: "border-amber-500 text-amber-900",
  },
  {
    swatch: "bg-violet-300",
    free: "bg-violet-100",
    booked: "border-violet-400 bg-violet-300 text-violet-900",
    request: "border-violet-500 text-violet-900",
  },
  {
    swatch: "bg-pink-300",
    free: "bg-pink-100",
    booked: "border-pink-400 bg-pink-300 text-pink-900",
    request: "border-pink-500 text-pink-900",
  },
  {
    swatch: "bg-teal-300",
    free: "bg-teal-100",
    booked: "border-teal-400 bg-teal-300 text-teal-900",
    request: "border-teal-500 text-teal-900",
  },
];

/** A single tutor's or a room's look: green free time, gray bookings. */
const ONE_LANE = {
  free: "bg-green-50",
  booked: "border-gray-300 bg-gray-200 text-gray-700",
  request: "border-gray-500 text-gray-700",
};

const BADGE_TONES = {
  free: "bg-green-100 text-green-700",
  some: "bg-amber-100 text-amber-800",
  busy: "bg-gray-200 text-gray-600",
};

const px = (minutes: number) => (minutes / 60) * HOUR_PX;

/**
 * Clock minutes from the start of `day`, clamped to the day. Counting clock
 * time (not elapsed time) keeps daylight saving days in line with the labels.
 */
export const minutesInto = (day: moment.Moment, time: moment.Moment) =>
  Math.min(
    24 * 60,
    Math.max(0, time.diff(day, "minutes") + time.utcOffset() - day.utcOffset())
  );

/** Whether a span overlaps any of the blocks. */
export const overlaps = (span: Span, blocks: Span[]) =>
  blocks.some((block) => block.from < span.to && block.to > span.from);

/** Whether a span lies within one stretch of free time. */
export const fits = (span: Span, free: Span[]) =>
  free.some((stretch) => stretch.from <= span.from && span.to <= stretch.to);

/** A time clashes in a column over a booking or outside a tutor's free time. */
const clashes = (span: Span, column: Column) =>
  column.lanes.some(
    (lane) =>
      overlaps(span, lane.blocks) || (lane.free && !fits(span, lane.free))
  );

/**
 * Places overlapping blocks side by side. Blocks must be sorted by start.
 * Returns each block's lane and the number of lanes in its overlap group.
 */
function layout(blocks: Block[]) {
  const lanes: number[] = [];
  const counts: number[] = new Array(blocks.length).fill(1);
  let laneEnds: number[] = [];
  let groupStart = 0;
  let groupEnd = 0;
  blocks.forEach((block, i) => {
    if (block.from >= groupEnd) {
      counts.fill(Math.max(1, laneEnds.length), groupStart, i);
      laneEnds = [];
      groupStart = i;
    }
    let lane = laneEnds.findIndex((end) => end <= block.from);
    if (lane === -1) lane = laneEnds.length;
    laneEnds[lane] = block.to;
    lanes[i] = lane;
    groupEnd = Math.max(groupEnd, block.to);
  });
  counts.fill(Math.max(1, laneEnds.length), groupStart);
  return { lanes, counts };
}

/** This meeting being dragged: where it is now, and where it was grabbed. */
type Drag = {
  key: string;
  from: number;
  /** Minutes from the box's top to the pointer. */
  grab: number;
  startX: number;
  startY: number;
  moved: boolean;
};

/**
 * The Book Meeting grids' day columns (rooms of one day, or a tutor's days):
 * an hour gutter, bookings as gray busy blocks, free time in green, This
 * meeting outlined, and a click picking the time in 15-minute steps. This
 * meeting can be dragged to another time or column (a busy one too, where it
 * turns red); the drop picks that column and start, keeping the length.
 */
export default function TimeGrid({
  columns,
  loading,
  focusMinutes,
  columnsInView,
  hoverMinutes,
  onPick,
}: {
  columns: Column[];
  loading: boolean;
  /** Opens about an hour above this time of day. */
  focusMinutes: number;
  /**
   * The calendar pop-up: each column takes this share of the width and the
   * rest scroll sideways. Otherwise the columns share the width.
   */
  columnsInView?: number;
  /**
   * Hovering marks the time a click would book (this meeting's length from
   * the 15-minute step under the pointer): green, or red when it overlaps a
   * booking or leaves a tutor's free time. It replaces the column's and the
   * free time's own hover color.
   */
  hoverMinutes?: number;
  /** A click on free time, or This meeting dropped: the column and start. */
  onPick: (column: Column, minutes: number) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  // The columns' bodies, to find the one under the pointer while dragging.
  const rowRef = useRef<HTMLDivElement>(null);
  const bodies = useRef(new Map<string, HTMLDivElement>());
  const [drag, setDrag] = useState<Drag | null>(null);
  const [hover, setHover] = useState<{ key: string; from: number } | null>(
    null
  );
  // Titles wrap in full, so every header takes the tallest one's height and
  // the hours stay in line across columns.
  const headers = useRef(new Map<string, HTMLDivElement>());
  const [headerPx, setHeaderPx] = useState(HEADER_PX);
  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    const measure = () =>
      setHeaderPx(
        Math.max(
          HEADER_PX,
          ...Array.from(headers.current.values(), (el) => el.offsetHeight)
        )
      );
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, [columns]);

  const home = columns.find((column) => column.thisMeeting);
  const length = home?.thisMeeting
    ? home.thisMeeting.to - home.thisMeeting.from
    : 0;

  const startDrag = (e: PointerEvent<HTMLDivElement>, column: Column) => {
    const box = column.thisMeeting;
    const body = bodies.current.get(column.key);
    if (e.button !== 0 || !box || !body) return;
    e.preventDefault();
    e.stopPropagation();
    // The row of columns keeps the pointer while the box moves between
    // columns (each move renders it anew).
    rowRef.current?.setPointerCapture(e.pointerId);
    const at = ((e.clientY - body.getBoundingClientRect().top) / HOUR_PX) * 60;
    setDrag({
      key: column.key,
      from: box.from,
      grab: at - box.from,
      startX: e.clientX,
      startY: e.clientY,
      moved: false,
    });
  };

  // Where the box would land with the pointer at (x, y).
  const dragTo = (x: number, y: number) => {
    if (!drag) return;
    const over = columns.find((column) => {
      const rect = bodies.current.get(column.key)?.getBoundingClientRect();
      return rect && x >= rect.left && x < rect.right;
    });
    const key = over?.key ?? drag.key;
    const body = bodies.current.get(key);
    if (!body) return;
    const at = ((y - body.getBoundingClientRect().top) / HOUR_PX) * 60;
    const from = Math.min(
      24 * 60 - length,
      Math.max(0, Math.round((at - drag.grab) / SLOT_MINUTES) * SLOT_MINUTES)
    );
    const moved =
      drag.moved || Math.hypot(x - drag.startX, y - drag.startY) >= DRAG_PIXELS;
    if (key !== drag.key || from !== drag.from || moved !== drag.moved) {
      setDrag({ ...drag, key, from, moved });
    }
  };
  const dragToRef = useRef(dragTo);
  dragToRef.current = dragTo;
  const pointer = useRef<{ x: number; y: number } | null>(null);
  const dragMoved = useRef(false);
  dragMoved.current = Boolean(drag?.moved);

  const moveDrag = (e: PointerEvent<HTMLDivElement>) => {
    if (!drag) return;
    pointer.current = { x: e.clientX, y: e.clientY };
    dragTo(e.clientX, e.clientY);
  };

  // While This meeting is dragged near or past an edge, the grid scrolls
  // that way (faster the further past), and the box follows the pointer.
  const dragging = drag !== null;
  useEffect(() => {
    if (!dragging) return;
    let frame = 0;
    const speed = (into: number) =>
      Math.min(EDGE_SCROLL_PX, Math.max(1, Math.ceil(into / 3)));
    const step = () => {
      const el = scrollRef.current;
      const at = pointer.current;
      if (el && at && dragMoved.current) {
        const rect = el.getBoundingClientRect();
        const top = rect.top + headerPx + EDGE_PX;
        const bottom = rect.bottom - EDGE_PX;
        const dy =
          at.y < top
            ? -speed(top - at.y)
            : at.y > bottom
            ? speed(at.y - bottom)
            : 0;
        let dx = 0;
        if (columnsInView) {
          const left = rect.left + GUTTER_PX + EDGE_PX;
          const right = rect.right - EDGE_PX;
          dx =
            at.x < left
              ? -speed(left - at.x)
              : at.x > right
              ? speed(at.x - right)
              : 0;
        }
        const before = [el.scrollTop, el.scrollLeft];
        el.scrollTop += dy;
        el.scrollLeft += dx;
        if (el.scrollTop !== before[0] || el.scrollLeft !== before[1]) {
          dragToRef.current(at.x, at.y);
        }
      }
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [dragging, headerPx, columnsInView]);

  const endDrag = () => {
    if (!drag) return;
    const column = columns.find((c) => c.key === drag.key);
    if (drag.moved && column) choose(column, drag.from);
    setDrag(null);
    pointer.current = null;
  };

  // The grid scrolls to the focus when it opens and when the time is set
  // elsewhere (the form), but not after a click or drop in the grid itself
  // (only a drag held at an edge scrolls it, above).
  const pickedAt = useRef<number | null>(null);
  const choose = (column: Column, minutes: number) => {
    pickedAt.current = minutes;
    onPick(column, minutes);
  };
  useEffect(() => {
    const fromHere = pickedAt.current === focusMinutes;
    pickedAt.current = null;
    if (!fromHere && scrollRef.current) {
      scrollRef.current.scrollTop = px(focusMinutes) - HOUR_PX;
    }
  }, [focusMinutes]);

  // With columns in view, it opens with This meeting's column (or the
  // highlighted day) in the middle. Only on opening: later picks keep the
  // view where it is.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !columnsInView) return;
    const at = columns.findIndex(
      (column) => column.thisMeeting || column.highlighted
    );
    if (at < 0) return;
    const width = (el.clientWidth - GUTTER_PX) / columnsInView;
    el.scrollLeft = Math.max(0, (at - Math.floor(columnsInView / 2)) * width);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pick = (column: Column, e: MouseEvent<HTMLDivElement>) => {
    const y = e.clientY - e.currentTarget.getBoundingClientRect().top;
    choose(
      column,
      Math.floor(((y / HOUR_PX) * 60) / SLOT_MINUTES) * SLOT_MINUTES
    );
  };

  return (
    <div ref={scrollRef} className="overflow-auto max-h-[55vh]">
      {/* The columns share the width, and scroll sideways only when it is
          too narrow for them; or, with columns in view, each takes its share
          and the rest scroll sideways (one column doesn't stretch). */}
      <div
        ref={rowRef}
        className={`flex ${
          columnsInView
            ? "w-[calc(48px_+_var(--columns)_*_(100%_-_48px)_/_var(--in-view))]"
            : "min-w-full"
        }`}
        style={
          columnsInView
            ? ({
                "--columns": columns.length,
                "--in-view": columnsInView,
              } as CSSProperties)
            : undefined
        }
        onPointerMove={moveDrag}
        onPointerUp={endDrag}
        onPointerCancel={() => setDrag(null)}
      >
        <div className="sticky left-0 z-20 w-12 shrink-0 bg-white">
          <div
            className="sticky top-0 z-10 bg-white"
            style={{ height: headerPx }}
          />
          {Array.from({ length: 24 }, (_, hour) => (
            <div
              key={hour}
              className="text-[11px] text-gray-500 pr-1 text-right -translate-y-2"
              style={{ height: HOUR_PX }}
            >
              {hour > 0 && moment().hour(hour).format("h A")}
            </div>
          ))}
        </div>
        {columns.map((column, index) => {
          // While dragging, the box shows where it would land.
          const dropped = drag && { from: drag.from, to: drag.from + length };
          const box = dropped
            ? drag.key === column.key && {
                ...dropped,
                clash: clashes(dropped, column),
              }
            : column.thisMeeting;
          const colors = (i: number) =>
            column.lanes.length === 1
              ? ONE_LANE
              : LANE_COLORS[i % LANE_COLORS.length];
          const laneWidth = 100 / column.lanes.length;
          return (
            <div
              key={column.key}
              className={`flex-1 ${
                columnsInView ? "min-w-0" : "min-w-[4.5rem]"
              }`}
            >
              {/* Hovering shows the full name and address at once, in a
                  white box (raised over the next columns' headers). */}
              <div
                className="group sticky top-0 z-10 hover:z-30 bg-white border-b border-gray-200"
                style={{ height: headerPx }}
              >
                <div
                  ref={(el) => {
                    if (el) headers.current.set(column.key, el);
                    else headers.current.delete(column.key);
                  }}
                  className="px-1 pb-1"
                >
                  <p
                    className={`text-[13px] font-bold leading-tight break-words ${
                      column.highlighted ? "text-green-700" : ""
                    }`}
                  >
                    {column.title}
                  </p>
                  {/* In a narrow column the date gives way; the badge keeps its
                    width unless it alone is too wide, then it is cut with
                    "…" (its full text on hover). */}
                  <p className="flex min-w-0 items-center gap-1 text-[11px] text-gray-500">
                    <span className="min-w-0 truncate">{column.subtitle}</span>
                    {!loading && column.badge && (
                      <span
                        className={`max-w-full shrink-0 truncate rounded-full px-1.5 font-semibold ${
                          BADGE_TONES[column.badge.tone]
                        }`}
                        title={column.badge.text}
                      >
                        {column.badge.text}
                      </span>
                    )}
                  </p>
                </div>
                <div
                  role="tooltip"
                  className={`pointer-events-none absolute top-full mt-1 hidden w-max max-w-[16rem] whitespace-pre-line rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 shadow-md group-hover:block ${
                    // Kept inside the grid: the right half opens leftward.
                    index < columns.length / 2 ? "left-1" : "right-1"
                  }`}
                >
                  {column.hint}
                </div>
              </div>
              <div
                ref={(el) => {
                  if (el) bodies.current.set(column.key, el);
                  else bodies.current.delete(column.key);
                }}
                className={`relative cursor-pointer border-l border-gray-100 ${
                  hoverMinutes || column.lanes.some((lane) => lane.free)
                    ? ""
                    : "hover:bg-green-50"
                }`}
                style={{
                  height: 24 * HOUR_PX,
                  backgroundImage: `repeating-linear-gradient(to bottom, #f3f4f6 0, #f3f4f6 1px, transparent 1px, transparent ${HOUR_PX}px)`,
                }}
                title={column.pickTitle}
                onClick={(e) => pick(column, e)}
                onPointerMove={(e) => {
                  if (!hoverMinutes) return;
                  const y =
                    e.clientY - e.currentTarget.getBoundingClientRect().top;
                  const from = Math.min(
                    24 * 60 - hoverMinutes,
                    Math.max(
                      0,
                      Math.floor(((y / HOUR_PX) * 60) / SLOT_MINUTES) *
                        SLOT_MINUTES
                    )
                  );
                  if (hover?.key !== column.key || hover.from !== from) {
                    setHover({ key: column.key, from });
                  }
                }}
                onPointerLeave={() => setHover(null)}
              >
                {loading && (
                  <div className="absolute inset-x-1 top-2 h-2 rounded bg-gray-100 animate-pulse" />
                )}
                {/* Free time first, then the hover box, then the bookings on
                    top of both. */}
                {column.lanes.map((lane, l) => (
                  <Fragment key={lane.key}>
                    {lane.free?.map((span) => (
                      <div
                        key={span.from}
                        className={`absolute ${colors(l).free} ${
                          // A single tutor's free time darkens under the
                          // pointer, unless the hover box shows the time.
                          column.lanes.length === 1 && !hoverMinutes
                            ? "hover:bg-green-100"
                            : ""
                        }`}
                        style={{
                          top: px(span.from),
                          height: px(span.to - span.from),
                          left: `${l * laneWidth}%`,
                          width: `${laneWidth}%`,
                        }}
                      />
                    ))}
                  </Fragment>
                ))}
                {!!hoverMinutes &&
                  !drag &&
                  hover?.key === column.key &&
                  (() => {
                    // The time a click would book: green, or red when it
                    // overlaps a booking (or, for a tutor, leaves their free
                    // time).
                    const span = {
                      from: hover.from,
                      to: hover.from + hoverMinutes,
                    };
                    return (
                      <div
                        className={`pointer-events-none absolute inset-x-0 ${
                          clashes(span, column) ? "bg-red-100" : "bg-green-100"
                        }`}
                        style={{
                          top: px(span.from),
                          height: px(hoverMinutes),
                        }}
                      />
                    );
                  })()}
                {column.lanes.map((lane, l) => {
                  const { lanes, counts } = layout(lane.blocks);
                  return (
                    <Fragment key={lane.key}>
                      {lane.blocks.map((block, i) => (
                        <div
                          key={block.id}
                          className={`absolute overflow-hidden rounded-md border px-1 text-[11px] leading-tight ${
                            block.pending
                              ? `border-dashed bg-white ${colors(l).request}`
                              : colors(l).booked
                          }`}
                          title={block.label}
                          style={{
                            top: px(block.from),
                            height: Math.max(
                              px(block.to - block.from),
                              MIN_BLOCK_PX
                            ),
                            left: `${(l + lanes[i] / counts[i]) * laneWidth}%`,
                            width: `${laneWidth / counts[i]}%`,
                          }}
                        >
                          {block.label}
                        </div>
                      ))}
                    </Fragment>
                  );
                })}
                {box && (
                  <div
                    className={`absolute inset-x-1 touch-none select-none rounded-md border-2 bg-white/80 px-1 text-[11px] font-semibold leading-tight ${
                      // Kept inside the box; a short one gets one line, cut.
                      box.to - box.from < 45 ? "truncate" : "overflow-hidden"
                    } ${
                      box.clash
                        ? "border-red-500 text-red-600"
                        : "border-green-500 text-green-700"
                    } ${drag ? "cursor-grabbing shadow-md" : "cursor-grab"}`}
                    style={{ top: px(box.from), height: px(box.to - box.from) }}
                    title="Drag to move"
                    onPointerDown={(e) => startDrag(e, column)}
                    // A press on the box isn't a click on the column.
                    onClick={(e) => e.stopPropagation()}
                  >
                    {drag
                      ? moment()
                          .startOf("day")
                          .minutes(drag.from)
                          .format("h:mm A")
                      : "This meeting"}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The frame around a grid: a header (arrows, what is shown, the legend and,
 * in the calendar pop-up, close) and, when loading failed, a Retry line in
 * place of the grid.
 */
export function GridPanel({
  label,
  previous,
  next,
  pager,
  showsFree = false,
  roomLegend = false,
  legend,
  failed,
  failedText,
  onRetry,
  close,
  children,
}: {
  label: string;
  previous?: { label: string; onClick: () => void; disabled?: boolean };
  next?: { label: string; onClick: () => void; disabled?: boolean };
  /** A second set of arrows after the label, such as for paging rooms. */
  pager?: {
    label: string;
    previous: { label: string; onClick: () => void; disabled?: boolean };
    next: { label: string; onClick: () => void; disabled?: boolean };
  };
  /** The legend also explains free time. */
  showsFree?: boolean;
  /** Rooms' legend: Available (white) and Booked (gray). */
  roomLegend?: boolean;
  /** Several tutors' colors, on a row under the header (in place of Free and Booked). */
  legend?: ReactNode;
  failed: boolean;
  failedText: string;
  onRetry: () => void;
  /** Only in the calendar pop-up, where the grid takes the form's place. */
  close?: { label: string; onClick: () => void };
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col min-w-0 bg-white rounded-lg border border-gray-200 p-3">
      <div className="flex flex-wrap items-center gap-2 pb-2">
        {previous && (
          <button
            type="button"
            aria-label={previous.label}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
            disabled={previous.disabled}
            onClick={previous.onClick}
          >
            <ChevronLeftIcon />
          </button>
        )}
        <p className="font-bold text-sm">{label}</p>
        {next && (
          <button
            type="button"
            aria-label={next.label}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
            disabled={next.disabled}
            onClick={next.onClick}
          >
            <ChevronRightIcon />
          </button>
        )}
        {pager && (
          <span className="flex items-center gap-1 text-xs text-gray-600">
            <button
              type="button"
              aria-label={pager.previous.label}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
              disabled={pager.previous.disabled}
              onClick={pager.previous.onClick}
            >
              <ChevronLeftIcon />
            </button>
            {pager.label}
            <button
              type="button"
              aria-label={pager.next.label}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
              disabled={pager.next.disabled}
              onClick={pager.next.onClick}
            >
              <ChevronRightIcon />
            </button>
          </span>
        )}
        <span className="ml-auto flex items-center gap-1 text-xs text-gray-700">
          {roomLegend ? (
            <>
              <span className="inline-block w-3 h-3 rounded-sm bg-white border border-gray-400" />
              <span className="mr-2">Available</span>
              <span className="inline-block w-3 h-3 rounded-sm bg-gray-200 border border-gray-300" />
              <span className="mr-2">Booked</span>
            </>
          ) : (
            !legend && (
              <>
                {showsFree && (
                  <>
                    <span className="inline-block w-3 h-3 rounded-sm bg-green-50 border border-green-200" />
                    <span className="mr-2">Free</span>
                  </>
                )}
                <span className="inline-block w-3 h-3 rounded-sm bg-gray-200 border border-gray-300" />
                <span className="mr-2">Booked</span>
                {showsFree && (
                  <>
                    <span className="inline-block w-3 h-3 rounded-sm bg-white border border-dashed border-gray-500" />
                    <span className="mr-2">Request</span>
                  </>
                )}
              </>
            )
          )}
          <span className="inline-block w-3 h-3 rounded-sm border-2 border-green-500" />
          This meeting
        </span>
        {close && (
          <button
            type="button"
            aria-label={close.label}
            className="p-1 rounded-full hover:bg-gray-100"
            onClick={close.onClick}
          >
            <Cross2Icon />
          </button>
        )}
      </div>

      {legend && (
        <div className="flex flex-wrap items-center gap-x-1 gap-y-1 pb-2 text-xs text-gray-600">
          {legend}
        </div>
      )}

      {failed ? (
        <p className="py-6 text-center text-sm text-gray-500">
          {failedText}{" "}
          <button
            type="button"
            className="text-green-600 font-bold hover:underline"
            onClick={onRetry}
          >
            Retry
          </button>
        </p>
      ) : (
        children
      )}
    </div>
  );
}
